import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { createProperty } from '../../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function AddPropertyModal({ open, onClose, onSuccess }) {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    landlord_id: '',
    address: '',
    suburb: '',
    state: 'VIC',
    postcode: '',
    property_type: 'apartment',
    bedrooms: 2,
    bathrooms: 1,
    parking: 1,
    rent_amount: 0,
    rent_frequency: 'weekly',
    description: ''
  });

  useEffect(() => {
    if (open) {
      loadLandlords();
    }
  }, [open]);

  const loadLandlords = async () => {
    try {
      const token = localStorage.getItem('token');
      const [landlordsRes, usersRes] = await Promise.all([
        axios.get(`${API}/landlords`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Map landlords with user names
      const landlordsWithNames = landlordsRes.data.map(landlord => {
        const user = usersRes.data.find(u => u.id === landlord.user_id);
        return {
          ...landlord,
          name: user?.name || 'Unknown',
          email: user?.email || ''
        };
      });
      
      setLandlords(landlordsWithNames);
    } catch (error) {
      console.error('Failed to load landlords:', error);
      toast.error('Failed to load landlords');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await createProperty({
        ...formData,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        parking: parseInt(formData.parking),
        rent_amount: parseFloat(formData.rent_amount)
      });
      
      toast.success('Property created successfully!');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        landlord_id: '',
        address: '',
        suburb: '',
        state: 'VIC',
        postcode: '',
        property_type: 'apartment',
        bedrooms: 2,
        bathrooms: 1,
        parking: 1,
        rent_amount: 0,
        rent_frequency: 'weekly',
        description: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Property</DialogTitle>
          <DialogDescription>Enter property details below</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {landlords.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-900 font-semibold mb-2">⚠️ No Landlords Available</p>
              <p className="text-sm text-yellow-800">
                You need to create a landlord first before adding properties. 
                Go to <strong>Landlords</strong> page and click <strong>"Add Landlord"</strong>.
              </p>
            </div>
          ) : (
            <div>
              <Label htmlFor="landlord">Property Owner (Landlord) *</Label>
              <Select
                value={formData.landlord_id}
                onValueChange={(value) => setFormData({ ...formData, landlord_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property owner..." />
                </SelectTrigger>
                <SelectContent>
                  {landlords.map((landlord) => (
                    <SelectItem key={landlord.id} value={landlord.user_id}>
                      {landlord.name} ({landlord.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {landlords.length} landlord{landlords.length !== 1 ? 's' : ''} available
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="address">Address *</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main Street"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="suburb">Suburb *</Label>
              <Input
                id="suburb"
                value={formData.suburb}
                onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                placeholder="Melbourne"
                required
              />
            </div>
            <div>
              <Label htmlFor="state">State *</Label>
              <Select
                value={formData.state}
                onValueChange={(value) => setFormData({ ...formData, state: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="VIC">VIC</SelectItem>
                  <SelectItem value="NSW">NSW</SelectItem>
                  <SelectItem value="QLD">QLD</SelectItem>
                  <SelectItem value="SA">SA</SelectItem>
                  <SelectItem value="WA">WA</SelectItem>
                  <SelectItem value="TAS">TAS</SelectItem>
                  <SelectItem value="NT">NT</SelectItem>
                  <SelectItem value="ACT">ACT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="postcode">Postcode *</Label>
              <Input
                id="postcode"
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                placeholder="3000"
                required
              />
            </div>
            <div>
              <Label htmlFor="property_type">Property Type *</Label>
              <Select
                value={formData.property_type}
                onValueChange={(value) => setFormData({ ...formData, property_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="townhouse">Townhouse</SelectItem>
                  <SelectItem value="unit">Unit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bedrooms">Bedrooms *</Label>
              <Input
                id="bedrooms"
                type="number"
                min="0"
                value={formData.bedrooms}
                onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="bathrooms">Bathrooms *</Label>
              <Input
                id="bathrooms"
                type="number"
                min="0"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="parking">Parking</Label>
              <Input
                id="parking"
                type="number"
                min="0"
                value={formData.parking}
                onChange={(e) => setFormData({ ...formData, parking: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rent_amount">Rent Amount *</Label>
              <Input
                id="rent_amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.rent_amount}
                onChange={(e) => setFormData({ ...formData, rent_amount: e.target.value })}
                placeholder="500"
                required
              />
            </div>
            <div>
              <Label htmlFor="rent_frequency">Frequency *</Label>
              <Select
                value={formData.rent_frequency}
                onValueChange={(value) => setFormData({ ...formData, rent_frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Modern property with great features..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || landlords.length === 0}>
              {loading ? 'Creating...' : landlords.length === 0 ? 'Create Landlord First' : 'Create Property'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}